import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { History, RefreshCw, ExternalLink, Eye } from 'lucide-react';
import JobCard from '../components/JobCard';
import axios from 'axios';
import toast from 'react-hot-toast';

const JobHistory = () => {
  const [searchParams] = useSearchParams();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isPolling, setIsPolling] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);
  const pollingIntervalRef = useRef(null);

  const fetchJobs = async (showToastOnError = true) => {
    try {
      const response = await axios.get('/api/jobs', { withCredentials: true });
      const newJobs = response.data.jobs;
      
      // Check if any jobs are actively processing
      const hasActiveJobs = newJobs.some(job => 
        job.status === 'pending' || job.status === 'processing'
      );
      
      setJobs(newJobs);
      setLastUpdate(new Date());
      
      return hasActiveJobs;
    } catch (error) {
      if (showToastOnError) {
        toast.error('Failed to load job history');
      }
      return false;
    } finally {
      if (loading) {
        setLoading(false);
      }
    }
  };

  const startPolling = () => {
    if (pollingIntervalRef.current) return; // Already polling
    
    setIsPolling(true);
    pollingIntervalRef.current = setInterval(async () => {
      const hasActiveJobs = await fetchJobs(false);
      
      // Stop polling if no active jobs
      if (!hasActiveJobs) {
        stopPolling();
      }
    }, 3000); // Poll every 3 seconds
  };

  const stopPolling = () => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    setIsPolling(false);
  };

  useEffect(() => {
    const initializePage = async () => {
      const hasActiveJobs = await fetchJobs();
      
      // Start polling if there are active jobs
      if (hasActiveJobs) {
        startPolling();
      }
    };
    
    initializePage();
    
    // Cleanup polling on unmount
    return () => {
      stopPolling();
    };
  }, [searchParams]);

  // Manual refresh function
  const handleRefresh = async () => {
    setLoading(true);
    const hasActiveJobs = await fetchJobs();
    
    if (hasActiveJobs && !isPolling) {
      startPolling();
    } else if (!hasActiveJobs && isPolling) {
      stopPolling();
    }
  };



  if (loading) {
    return (
      <div className="text-center py-12">
        <History className="mx-auto h-12 w-12 text-gray-400 mb-4 animate-spin" />
        <p className="text-gray-600">Loading job history...</p>
      </div>
    );
  }

  if (jobs.length === 0) {
    return (
      <div className="text-center py-12">
        <History className="mx-auto h-12 w-12 text-gray-400 mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">No Job History</h3>
        <p className="text-gray-600">You haven't run any jobs yet.</p>
      </div>
    );
  }

  return (
    <div className="py-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Job History</h1>
        <div className="flex items-center space-x-4">
          {/* Live update indicator */}
          {isPolling && (
            <div className="flex items-center text-sm text-green-600">
              <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
              Live updates active
            </div>
          )}
          
          {/* Last update time */}
          {lastUpdate && (
            <div className="text-sm text-gray-500">
              Last updated: {lastUpdate.toLocaleTimeString()}
            </div>
          )}
          
          {/* Manual refresh button */}
          <button
            onClick={handleRefresh}
            disabled={loading}
            className="flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>
      
      <div className="space-y-4">
        {jobs.map((job) => {
          const isHighlighted = searchParams.get('job') === job._id;
          
          return (
            <JobCard
              key={job._id}
              job={job}
              variant="detailed"
              isHighlighted={isHighlighted}
            />
          );
        })}
      </div>
    </div>
  );
};

export default JobHistory;
